// Immunocal SG — Custom hero (fade carousel)
// Crossfades slides, configurable autoplay, optional dots/arrows/progress,
// touch swipe, pause on hover / when tab hidden. Slides are the child blocks.

class CustomHero extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hero-track]');
    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.dotsWrap = this.querySelector('[data-hero-dots]');
    this.count = this.slides.length;
    if (this.count === 0) return;

    this.index = 0;
    this.autoplay = this.dataset.autoplay === 'true';
    this.interval = parseInt(this.dataset.interval, 10) || 5000;
    this.pauseOnHover = this.dataset.pauseHover === 'true';
    this.loop = this.dataset.loop !== 'false';
    this.timer = null;

    this.buildDots();
    this.bindArrows();
    this.bindSwipe();
    this.bindPausing();

    this.track?.setAttribute('data-hero-ready', '');
    const startIndex = (!this.autoplay && this.count > 1) ? Math.floor(Math.random() * this.count) : 0;
    this.setActive(startIndex);
    this.maybeStart();
  }

  disconnectedCallback() {
    this.stop();
  }

  /* ---- state ---- */
  setActive(i) {
    if (this.loop) {
      this.index = (i + this.count) % this.count;
    } else {
      this.index = Math.max(0, Math.min(i, this.count - 1));
    }
    this.slides.forEach((s, idx) => s.classList.toggle('is-active', idx === this.index));
    if (this.dots) {
      this.dots.forEach((d, idx) => d.setAttribute('aria-current', idx === this.index ? 'true' : 'false'));
    }
  }

  next() { this.setActive(this.index + 1); }
  prev() { this.setActive(this.index - 1); }

  goTo(i) {
    this.setActive(i);
    this.restart();
  }

  /* ---- autoplay ---- */
  maybeStart() {
    if (this.autoplay && this.count > 1) this.start();
  }

  start() {
    this.stop();
    this.timer = setInterval(() => this.next(), this.interval);
  }

  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    if (this.autoplay && this.count > 1) this.start();
  }

  /* ---- controls ---- */
  buildDots() {
    if (!this.dotsWrap) return;
    this.dots = [];
    this.slides.forEach((_, idx) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'custom-hero__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to slide ${idx + 1}`);
      dot.addEventListener('click', () => this.goTo(idx));
      this.dotsWrap.appendChild(dot);
      this.dots.push(dot);
    });
  }

  bindArrows() {
    this.querySelector('[data-hero-prev]')?.addEventListener('click', () => {
      this.prev();
      this.restart();
    });
    this.querySelector('[data-hero-next]')?.addEventListener('click', () => {
      this.next();
      this.restart();
    });
  }

  bindSwipe() {
    let startX = 0;
    let active = false;
    const viewport = this.querySelector('[data-hero-viewport]') || this;

    viewport.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse') return; // let mouse clicks work normally
      startX = e.clientX;
      active = true;
    });
    viewport.addEventListener('pointerup', (e) => {
      if (!active) return;
      active = false;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 45) {
        dx < 0 ? this.next() : this.prev();
        this.restart();
      }
    });
    viewport.addEventListener('pointercancel', () => { active = false; });
  }

  bindPausing() {
    if (this.pauseOnHover && this.autoplay) {
      this.addEventListener('pointerenter', () => this.stop());
      this.addEventListener('pointerleave', () => this.restart());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.restart());
    }
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else this.maybeStart();
    });
  }
}

if (!customElements.get('custom-hero')) {
  customElements.define('custom-hero', CustomHero);
}
